import _ from "lodash";
import React, {
  useRef,
  useCallback,
  useLayoutEffect,
  useEffect,
  memo,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { compose } from "lib/apolloClient";
import { checkScrollAtBottom } from "services/checkScrollAtBottom";
import Loading from "UIComponents/Loading";
import { useHandleScroll, useDebouncedCallback } from "Utils/CustomHooks";
import Messages from "MessagingV2/components/Messages";
import {
  markConversationAsRead,
  setPreservedScrollMessageId,
  setLastReadMessage,
  setShowUnreadMessageButton,
  setShowSummariesNewMessagesWithAi,
  userChatActivityLog,
} from "MessagingV2/modules/MessagingModule";
import {
  canAdminDeleteMessage,
  CHAT_TYPE,
  getIsUserChatActivityLogEnabled,
  getIsViewMode,
} from "MessagingV2/modules/Utils";
import {
  useChatOptions,
  withChatOptions,
} from "MessagingV2/providers/ChatOptionsProvider";
import { isIdEqual } from "Utils/idHelpers";
import { useDispatch } from "Hooks/useDispatch";

import classes from "./MessagesContainer.scss";

// safe to memoize getBoundingClientRect of scroll container, as it is
// never going to change position in DOM during the lifecycle of the component
const getBoundingClientRectMemo = _.memoize(el => el.getBoundingClientRect());

const getFirstVisibleMessageId = ({ container, visibleItems, buffer = 16 }) => {
  const containerBoundingClientRect = getBoundingClientRectMemo(container);

  const firstVisibleMessageEntry = _.find(
    visibleItems,
    ({ boundingClientRect }) => {
      const { bottom } = boundingClientRect;
      return bottom - buffer >= containerBoundingClientRect.top;
    }
  );
  if (!firstVisibleMessageEntry) return undefined;
  const messageId = _.get(firstVisibleMessageEntry, "target.dataset.message");
  return messageId;
};

const MessagesContainer = props => {
  const {
    conversations,
    chatType,
    hasNextPage,
    hasPreviousPage,
    chatPanelRef,
    handleLoadNewMessages,
    handleLoadOldMessages,
    setPreservedScrollMessageId,
    onParentMessageClick,
    onChangeMessageData,
    handleCreateMessage,
    lastReadMessage,
    isMoreMessagesLoading,
    highlightedMessageId,
    users,
    isArchived,
    activeUserId: currentUserId,
    isMessageOptionsEnabled = true,
    unreadMessageCount,
    handleLoadEndMessagePage,
    isUserCreator,
    viewOnlyUsers,
    userChatActivityLog,
    personalChatReceiverUserId,
  } = props;

  const { chatId, screenMode } = useChatOptions();
  const dispatch = useDispatch();
  const isViewMode = getIsViewMode({ screenMode });
  const scrollContainerRef = useRef(null);
  const isChat = chatType === CHAT_TYPE.CHAT;
  const isUserChatActivityLogEnabled = getIsUserChatActivityLogEnabled();
  const otherUsers = useMemo(
    () => _.filter(users, user => !isIdEqual(user.id, currentUserId)),
    [users, currentUserId]
  );
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  useEffect(() => {
    // Mark all messages as read on opening of chat(reference: Slack)
    if (!isViewMode) markConversationAsRead({ chatId });
  }, [chatId, isViewMode]);

  useEffect(() => {
    const logUserChatActivity = ({ activityType }) => {
      if (isUserChatActivityLogEnabled) {
        userChatActivityLog({
          chatId,
          activityType,
          userCount: _.size(users),
        });
      }
    };

    logUserChatActivity({ activityType: "ENTERED_CHAT" });

    return () => {
      logUserChatActivity({ activityType: "LEFT_CHAT" });
    };
  }, [chatId]);

  const handleBottomScroll = () => {
    const container = scrollContainerRef.current;
    const scrollBottom =
      container?.scrollHeight - container?.scrollTop - container?.clientHeight;

    if (scrollBottom > 200) {
      setShowScrollToBottom(true);
    } else {
      setShowScrollToBottom(false);
    }
  };

  const [recordFirstVisibleMessageId] = useDebouncedCallback(
    () => {
      const container = scrollContainerRef.current;

      handleBottomScroll();
      if (!container) return;
      if (checkScrollAtBottom(container)) {
        setPreservedScrollMessageId({ chatId, messageId: null });
        return;
      }

      const observer = new IntersectionObserver(
        entries => {
          const visibleItems = entries.filter(entry => entry.isIntersecting);
          const messageId = getFirstVisibleMessageId({
            container,
            visibleItems,
          });
          observer.disconnect();
          setPreservedScrollMessageId({ chatId, messageId });
        },
        { root: container }
      );

      _.forEach(container.querySelectorAll("[data-message]"), el => {
        observer.observe(el);
      });
    },
    300,
    [chatId, setPreservedScrollMessageId]
  );

  useLayoutEffect(() => {
    recordFirstVisibleMessageId();
  }, [chatId, recordFirstVisibleMessageId]);

  const onEditMessageClick = useCallback(
    ({ message }) => {
      const messageLabel = _.get(message, "label", "");
      const messageAttachments = _.get(message, "attachments", []);
      const id = _.get(message, "id", "");
      onChangeMessageData(chatId, {
        id,
        parentMessage: {},
        label: messageLabel,
        attachments: messageAttachments || [],
        isEditMode: true,
      });
      if (_.isFunction(chatPanelRef?.current?.setChatbarContent)) {
        chatPanelRef.current.setChatbarContent(messageLabel);
      }
    },
    [onChangeMessageData, chatId, chatPanelRef]
  );

  const onRetryMessageClick = useCallback(
    message => {
      const collaborators = isChat ? [] : otherUsers;
      handleCreateMessage({ ...message, chatId, collaborators });
    },
    [handleCreateMessage, isChat, otherUsers, chatId]
  );

  const onReplyMessageClick = useCallback(
    ({ message: parentMessage }) => {
      onChangeMessageData(chatId, {
        parentMessage,
        isEditMode: false,
      });
    },
    [onChangeMessageData, chatId]
  );

  const getScrollRef = () => {
    return scrollContainerRef.current;
  };

  useHandleScroll({
    networkStatus: isMoreMessagesLoading.previous ? 3 : 7,
    loadMoreFeed: handleLoadOldMessages,
    hasNextPage: hasPreviousPage,
    getScrollRef,
    isScrollAtBottom: false,
  });

  useHandleScroll({
    networkStatus: isMoreMessagesLoading.next ? 3 : 7,
    loadMoreFeed: handleLoadNewMessages,
    hasNextPage: hasNextPage,
    getScrollRef,
    isScrollAtBottom: true,
  });

  useEffect(() => {
    dispatch(setLastReadMessage({ chatId, lastReadMessage }));
    return () => {
      dispatch(setShowUnreadMessageButton(false));
      // to enable showing summaries with new messages with red line
      dispatch(setShowSummariesNewMessagesWithAi(true));
    };
  }, [chatId, dispatch]);

  const isUserPartOfChat = useMemo(
    () => isChat || _.find(users, { id: currentUserId }),
    [isChat, users, currentUserId]
  );
  const isMessageOptionsVisible =
    (!!isUserPartOfChat || (isViewMode && canAdminDeleteMessage())) &&
    !isArchived &&
    isMessageOptionsEnabled;

  return (
    <div className={classes.containerWrapper}>
      <div
        className={classes.container}
        ref={scrollContainerRef}
        onScroll={recordFirstVisibleMessageId}
        id={`chat-scroll-container-${chatId}`}
      >
        <div className={classes.spacer} />
        {isMoreMessagesLoading.previous ? (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        ) : null}
        <Messages
          chatType={chatType}
          messages={conversations}
          lastReadMessage={lastReadMessage}
          onEditMessageClick={onEditMessageClick}
          onRetryMessageClick={onRetryMessageClick}
          onReplyMessageClick={onReplyMessageClick}
          onParentMessageClick={onParentMessageClick}
          highlightedMessageId={highlightedMessageId}
          isMessageOptionsVisible={isMessageOptionsVisible}
          unreadMessageCount={unreadMessageCount}
          showScrollToBottom={showScrollToBottom}
          handleLoadEndMessagePage={handleLoadEndMessagePage}
          isUserCreator={isUserCreator}
          viewOnlyUsers={viewOnlyUsers}
          personalChatReceiverUserId={personalChatReceiverUserId}
        />
        {isMoreMessagesLoading.next ? (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        ) : null}
      </div>
    </div>
  );
};

MessagesContainer.propTypes = {
  chatType: PropTypes.string.isRequired,
  conversations: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  hasPreviousPage: PropTypes.bool.isRequired,
  handleLoadNewMessages: PropTypes.func.isRequired,
  handleLoadOldMessages: PropTypes.func.isRequired,
  onParentMessageClick: PropTypes.func.isRequired,
  isMoreMessagesLoading: PropTypes.shape({
    next: PropTypes.bool,
    previous: PropTypes.bool,
  }).isRequired,
  highlightedMessageId: PropTypes.string,
  onChangeMessageData: PropTypes.func.isRequired,
};

MessagesContainer.defaultProps = {
  highlightedMessageId: null,
};

const mapActionCreators = {
  setPreservedScrollMessageId,
  userChatActivityLog,
};

export default compose(
  withChatOptions(),
  connect(null, mapActionCreators),
  memo
)(MessagesContainer);
